/**
 * chat-empty-widgets-state.ts
 *
 * Persisted state for empty chat widgets: Trending visibility,
 * enabled widgets, order, and per-widget settings (city, tickers, etc.).
 */

export const EMPTY_CHAT_WIDGETS_STORAGE_KEY = 'losi_empty_chat_widgets'

export type EmptyChatWidgetId =
  | 'recent_chats'
  | 'pinned_chats'
  | 'starred_messages'
  | 'recent_notes'
  | 'clock'
  | 'sticky'
  | 'weather'
  | 'finance'
  | 'news'
  | 'shortcuts'  // displayed as "Prompts"; id kept for localStorage compatibility
  | 'sites'
  | 'nexus_agents'
  | 'nexus_crm'
  | 'nexus_integrations'
  | 'nexus_conversations'
  | 'nexus_notes'

export const NEXUS_EMPTY_CHAT_WIDGET_IDS = [
  'nexus_agents',
  'nexus_crm',
  'nexus_integrations',
  'nexus_conversations',
  'nexus_notes'
] as const satisfies readonly EmptyChatWidgetId[]

export interface EmptyChatWidgetSettings {
  clockCity?: string
  weatherCity?: string
  weatherPreciseLocation?: boolean
  financeTickers?: string[]
  newsCategory?: string
  stickyColor?: string
  /** Persistent sticky note content (per user, saved with widget state) */
  stickyContent?: string
}

/** Instance key for a widget at a given index (same type can appear multiple times). */
export function getInstanceKey(enabledWidgets: EmptyChatWidgetId[], index: number): string {
  const id = enabledWidgets[index]
  let occurrence = 0
  for (let j = 0; j <= index; j++) {
    if (enabledWidgets[j] === id) occurrence++
  }
  return `${id}_${occurrence - 1}`
}

/** Per-instance settings. Key = getInstanceKey(enabledWidgets, index). */
export type WidgetSettingsMap = Record<string, EmptyChatWidgetSettings>

export interface EmptyChatWidgetsState {
  showTrending: boolean
  enabledWidgets: EmptyChatWidgetId[]
  /** @deprecated Use widgetSettingsMap for per-instance settings. Kept for migration. */
  widgetSettings?: EmptyChatWidgetSettings
  /** Per-instance settings keyed by instance key (e.g. clock_0, news_1). */
  widgetSettingsMap?: WidgetSettingsMap
}

const VALID_WIDGET_IDS: EmptyChatWidgetId[] = [
  'recent_chats',
  'pinned_chats',
  'starred_messages',
  'recent_notes',
  'clock',
  'sticky',
  'weather',
  'finance',
  'news',
  'shortcuts',
  'sites',
  'nexus_agents',
  'nexus_crm',
  'nexus_integrations',
  'nexus_conversations',
  'nexus_notes'
]

const DEFAULT_ENABLED_WIDGETS: EmptyChatWidgetId[] = [
  'clock',
  'recent_chats',
  'sites',
  'shortcuts'
]

export const DEFAULT_EMPTY_CHAT_WIDGETS_STATE: EmptyChatWidgetsState = {
  showTrending: true,
  enabledWidgets: DEFAULT_ENABLED_WIDGETS,
  widgetSettingsMap: {}
}

export function isNexusEmptyChatWidgetId(id: EmptyChatWidgetId): boolean {
  return NEXUS_EMPTY_CHAT_WIDGET_IDS.includes(id as (typeof NEXUS_EMPTY_CHAT_WIDGET_IDS)[number])
}

export function filterEmptyChatWidgetsForNexusAccess(
  state: EmptyChatWidgetsState,
  hasNexusAccess: boolean
): EmptyChatWidgetsState {
  if (hasNexusAccess) return state

  return {
    ...state,
    enabledWidgets: state.enabledWidgets.filter((id) => !isNexusEmptyChatWidgetId(id))
  }
}

function migrateToSettingsMap(
  enabledWidgets: EmptyChatWidgetId[],
  flat: EmptyChatWidgetSettings | undefined
): WidgetSettingsMap {
  if (!flat || typeof flat !== 'object') return {}
  const map: WidgetSettingsMap = {}
  const firstOfType = new Set<EmptyChatWidgetId>()
  enabledWidgets.forEach((id, index) => {
    if (firstOfType.has(id)) return
    firstOfType.add(id)
    const key = getInstanceKey(enabledWidgets, index)
    const slice: EmptyChatWidgetSettings = {}
    if (id === 'clock' && flat.clockCity != null) slice.clockCity = flat.clockCity
    if (id === 'weather') {
      if (flat.weatherCity != null) slice.weatherCity = flat.weatherCity
      if (flat.weatherPreciseLocation != null) slice.weatherPreciseLocation = flat.weatherPreciseLocation
    }
    if (id === 'news' && flat.newsCategory != null) slice.newsCategory = flat.newsCategory
    if (id === 'finance' && flat.financeTickers != null) slice.financeTickers = flat.financeTickers
    if (id === 'sticky') {
      if (flat.stickyColor != null) slice.stickyColor = flat.stickyColor
      if (flat.stickyContent != null) slice.stickyContent = flat.stickyContent
    }
    if (Object.keys(slice).length > 0) map[key] = slice
  })
  return map
}

function parseFromObject(parsed: Partial<EmptyChatWidgetsState & { widgetSettings?: EmptyChatWidgetSettings }>): EmptyChatWidgetsState | null {
  if (typeof parsed.showTrending !== 'boolean' && parsed.showTrending !== undefined) return null
  if (!Array.isArray(parsed.enabledWidgets)) return null
  const enabledWidgets = parsed.enabledWidgets.filter((id): id is EmptyChatWidgetId =>
    VALID_WIDGET_IDS.includes(id as EmptyChatWidgetId)
  )
  const flat = parsed.widgetSettings && typeof parsed.widgetSettings === 'object' ? parsed.widgetSettings : undefined
  const mapFromFlat = migrateToSettingsMap(enabledWidgets, flat)
  const widgetSettingsMap: WidgetSettingsMap = {
    ...mapFromFlat,
    ...(parsed.widgetSettingsMap && typeof parsed.widgetSettingsMap === 'object' ? parsed.widgetSettingsMap : {})
  }
  return {
    showTrending: parsed.showTrending ?? DEFAULT_EMPTY_CHAT_WIDGETS_STATE.showTrending,
    enabledWidgets,
    widgetSettingsMap
  }
}

function parseStored(raw: string | null): EmptyChatWidgetsState | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<EmptyChatWidgetsState & { widgetSettings?: EmptyChatWidgetSettings }>
    return parseFromObject(parsed)
  } catch {
    return null
  }
}

/** Parse state from API (e.g. server-synced). Returns null if invalid. */
export function parseEmptyChatWidgetsStateFromApi(raw: unknown): EmptyChatWidgetsState | null {
  if (!raw || typeof raw !== 'object') return null
  return parseFromObject(raw as Partial<EmptyChatWidgetsState & { widgetSettings?: EmptyChatWidgetSettings }>)
}

export function getEmptyChatWidgetsState(): EmptyChatWidgetsState {
  if (typeof window === 'undefined') return DEFAULT_EMPTY_CHAT_WIDGETS_STATE
  const raw = localStorage.getItem(EMPTY_CHAT_WIDGETS_STORAGE_KEY)
  const parsed = parseStored(raw)
  if (!parsed) return DEFAULT_EMPTY_CHAT_WIDGETS_STATE
  return {
    ...DEFAULT_EMPTY_CHAT_WIDGETS_STATE,
    ...parsed,
    enabledWidgets: parsed.enabledWidgets.length ? parsed.enabledWidgets : DEFAULT_ENABLED_WIDGETS
  }
}

export function setEmptyChatWidgetsState(state: EmptyChatWidgetsState): void {
  if (typeof window === 'undefined') return
  try {
    const toSave: EmptyChatWidgetsState = {
      showTrending: state.showTrending,
      enabledWidgets: state.enabledWidgets,
      widgetSettingsMap: state.widgetSettingsMap ?? {}
    }
    localStorage.setItem(EMPTY_CHAT_WIDGETS_STORAGE_KEY, JSON.stringify(toSave))
  } catch {
    // ignore
  }
}
